import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useMotionValue, useAnimation } from "framer-motion";
import { MessageCircle, X, ChevronRight, ArrowLeft, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const WHATSAPP_NUMBER = "447738998165";
const PHONE_NUMBER = "07738998165";

const FAQ_ITEMS = [
  { q: "What time are the services?", a: "Our Sunday service runs from 11:00 AM to 1:00 PM every Sunday." },
  { q: "Where is the church located?", a: "Walton Community Hall, 274 High Street, Walton, Felixstowe, IP11 9DS." },
  { q: "How can I submit a prayer request?", a: "You can send your prayer request directly to Pastor Samson via WhatsApp using the button below." },
  { q: "How do I contact the pastor?", a: "You can reach Pastor Samson Jegede on 07738998165 or email dominionparish0@gmail.com." },
  { q: "Is there a children's church?", a: "Please contact us directly for details about our children's programmes." },
  { q: "How do I give tithes and offerings?", a: "Please speak to Pastor Samson or contact us via email for giving details." },
];

export default function LiveChatWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("home");
  const [activeFaq, setActiveFaq] = useState(null);
  const constraintsRef = useRef(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const controls = useAnimation();
  const btnRef = useRef(null);

  const magnetize = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const screenW = window.innerWidth;
    const margin = 16;
    const currentLeft = rect.left;
    const desiredLeft = centerX > screenW / 2 ? screenW - rect.width - margin : margin;
    const deltaX = desiredLeft - currentLeft;
    controls.start({
      x: x.get() + deltaX,
      transition: { type: "spring", stiffness: 200, damping: 30, mass: 0.8 },
    });
  }, [x, controls]);

  const openWhatsApp = (message) => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message || "Hi, I'd like to know more about Dominion Parish.")}`;
    window.open(url, "_blank");
  };

  const openFaq = (idx) => { setActiveFaq(idx); setView("faq-answer"); };

  return (
    <>
      <div ref={constraintsRef} className="fixed inset-0 pointer-events-none z-[60]" />
      <motion.div
        ref={btnRef}
        className="fixed bottom-8 right-6 z-[61] pointer-events-auto"
        drag
        dragConstraints={constraintsRef}
        dragElastic={0.05}
        dragMomentum={false}
        dragTransition={{ bounceStiffness: 300, bounceDamping: 30 }}
        onDragEnd={magnetize}
        style={{ x, y }}
        animate={controls}
        data-testid="live-chat-widget"
      >
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 10 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="absolute bottom-16 right-0 w-[340px] bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col"
            style={{ maxHeight: "min(580px, calc(100vh - 120px))" }}
            data-testid="chat-window"
            onPointerDown={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-navy px-5 py-4 flex items-center justify-between shrink-0">
              <div>
                <p className="font-heading font-bold text-white text-sm">Dominion Parish</p>
                <p className="text-white/50 text-xs font-body">How can we help?</p>
              </div>
              <button onClick={() => { setOpen(false); setView("home"); }} className="text-white/60 hover:text-white transition-colors" data-testid="chat-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* HOME VIEW */}
            {view === "home" && (
              <div className="flex-1 overflow-y-auto" data-testid="chat-home-view">
                <div className="px-5 pt-5 pb-3">
                  <p className="font-body text-slate-700 text-base">Hi there! How can we help you today?</p>
                </div>

                <div className="px-5 pb-3">
                  <p className="font-body font-bold text-slate-400 text-xs uppercase tracking-[0.15em] mb-3">Frequently Asked</p>
                  <div className="space-y-2">
                    {FAQ_ITEMS.map((faq, i) => (
                      <button
                        key={i}
                        onClick={() => openFaq(i)}
                        className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors text-left group"
                        data-testid={`faq-item-${i}`}
                      >
                        <span className="font-body text-slate-700 text-sm font-medium">{faq.q}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-navy shrink-0 ml-2 transition-colors" />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="px-5 pb-5 pt-2 border-t border-slate-100 mt-2 space-y-3">
                  <p className="font-body font-bold text-slate-400 text-xs uppercase tracking-[0.15em] mb-1">Contact Us</p>
                  <Button
                    onClick={() => openWhatsApp()}
                    className="w-full rounded-full bg-cgreen hover:bg-cgreen/90 text-white font-body text-sm"
                    data-testid="whatsapp-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" /> Chat on WhatsApp
                  </Button>
                  <a href={`tel:${PHONE_NUMBER}`} className="block">
                    <Button
                      variant="outline"
                      className="w-full rounded-full font-body text-sm border-slate-200"
                      data-testid="call-btn"
                    >
                      <Phone className="w-4 h-4 mr-2" /> Call Pastor Samson
                    </Button>
                  </a>
                </div>
              </div>
            )}

            {/* FAQ ANSWER VIEW */}
            {view === "faq-answer" && activeFaq !== null && (
              <div className="flex-1 overflow-y-auto p-5" data-testid="faq-answer-view">
                <button onClick={() => setView("home")} className="flex items-center gap-1 text-navy font-body text-sm font-semibold mb-4 hover:opacity-70 transition-opacity" data-testid="faq-back-btn">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <p className="font-heading font-bold text-navy text-base mb-3">{FAQ_ITEMS[activeFaq].q}</p>
                <p className="font-body text-slate-600 text-sm leading-relaxed">{FAQ_ITEMS[activeFaq].a}</p>
                <div className="mt-6 pt-4 border-t border-slate-100 space-y-3">
                  <p className="font-body text-slate-400 text-xs mb-1">Still need help?</p>
                  <Button
                    onClick={() => openWhatsApp(`Hi, I have a question about: ${FAQ_ITEMS[activeFaq].q}`)}
                    className="w-full rounded-full bg-cgreen hover:bg-cgreen/90 text-white font-body text-sm"
                    data-testid="faq-whatsapp-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" /> Ask on WhatsApp
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Toggle button with glowing rings */}
        <div className="relative">
          {!open && (
            <>
              <motion.span
                className="absolute inset-0 rounded-full border-2 border-navy/40"
                animate={{ scale: [1, 1.6], opacity: [0.5, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeOut" }}
              />
              <motion.span
                className="absolute inset-0 rounded-full border-2 border-navy/30"
                animate={{ scale: [1, 1.7], opacity: [0.4, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeOut", delay: 1 }}
              />
              <motion.span
                className="absolute inset-0 rounded-full border-2 border-navy/20"
                animate={{ scale: [1, 1.8], opacity: [0.3, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeOut", delay: 2 }}
              />
            </>
          )}
          <button
            onClick={() => setOpen(!open)}
            data-testid="chat-toggle-btn"
            className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 ${
              open ? "bg-crimson text-white shadow-crimson/30" : "bg-navy text-white shadow-[0_0_25px_rgba(27,20,100,0.5)]"
            }`}
          >
            {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
          </button>
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white bg-cgreen" />
        </div>
      </motion.div>
    </>
  );
}
